import type { components } from "./generated/openapi.js";

// How stages, decisions and agents read on screen and in messages (design v0.6,
// C2): one map each, shared by the API, the staff app and the portal, so a
// stored value like `interview` or `screening_agent` never shows as it is.
// test/labels.test.ts fails if a stage or decision type in the contract has no
// label here.

type Schemas = components["schemas"];
export type SubmissionStage = NonNullable<Schemas["Submission"]["stage"]>;
export type DecisionType = NonNullable<Schemas["Decision"]["type"]>;

export const STAGE_LABELS: Record<SubmissionStage, string> = {
  applied: "applied",
  sourced: "sourced",
  screened: "screened",
  shortlist: "shortlisted",
  client_review: "with client",
  interview: "interviewing",
  offer: "offer pending",
  placed: "placed",
  rejected: "not progressed",
  withdrawn: "withdrawn",
  not_selected: "not selected",
};

export const stageLabel = (stage: string | null | undefined) =>
  stage ? (STAGE_LABELS[stage as SubmissionStage] ?? stage.replaceAll("_", " ")) : "";

// Sentence-case verb phrases, used for a decision's title and its type chip.
export const DECISION_TITLES: Record<DecisionType, string> = {
  shortlist_ranking: "Move to shortlist",
  rejection: "Reject",
  placement: "Place",
  offer: "Move to offer",
  assignment_extension: "Extend assignment",
  assignment_end: "End assignment",
  adverse_action_pre: "Send pre-adverse notice",
  adverse_action_final: "Send final adverse notice",
  payroll_batch: "Approve payroll batch",
  off_cycle_payment: "Approve off-cycle payment",
  vms_submittal: "Submit to VMS",
  privacy_response: "Respond to privacy request",
  invoice_batch: "Approve invoices",
};

// A ranking proposed for a whole job (sourcing_agent's long list) isn't a move.
export const decisionTitle = (type: string | null | undefined, subjectObject?: string) =>
  type === "shortlist_ranking" && subjectObject === "job"
    ? "Rank the long list"
    : (DECISION_TITLES[type as DecisionType] ?? (type ?? "").replaceAll("_", " "));

// Agents by their assistant names; the "(AI)" tag is added where they're shown.
export const AGENT_NAMES: Record<string, string> = {
  billing_agent: "Billing assistant",
  comms_agent: "Messaging assistant",
  dedupe_agent: "Duplicates assistant",
  document_agent: "Document assistant",
  examples_agent: "Examples assistant",
  extension_agent: "Extension assistant",
  import_agent: "Import assistant",
  intake_agent: "Intake assistant",
  onboarding_agent: "Onboarding assistant",
  ops_agent: "Operations assistant",
  outreach_agent: "Outreach assistant",
  payroll_agent: "Payroll assistant",
  privacy_agent: "Privacy assistant",
  scheduling_agent: "Scheduling assistant",
  screening_agent: "Screening assistant",
  skills_agent: "Skills assistant",
  sourcing_agent: "Sourcing assistant",
  style_agent: "Writing-style assistant",
  submission_agent: "Submission assistant",
  timesheet_agent: "Timesheet assistant",
  vms_agent: "VMS assistant",
};

// "screening_agent" → "Screening assistant"; an unknown agent still reads as one.
export function agentName(agent: string | null | undefined): string {
  if (!agent) return "AI assistant";
  const known = AGENT_NAMES[agent];
  if (known) return known;
  const base = agent.replace(/_agent$/, "").replaceAll("_", " ");
  return `${base.charAt(0).toUpperCase()}${base.slice(1)} assistant`;
}
